import { motion } from "framer-motion";
import { ArrowRight, Sparkles, Star, Users, MapPin } from "lucide-react";
import { Link } from "react-router-dom";
import Navigation from "../components/shared/Navigation";
import Footer from "../components/shared/Footer";
import PageBreadcrumb from "../components/shared/PageBreadcrumb";
import { ClickableImage } from "../components/shared/ImageLightbox";
import { usePageMeta, generateServiceSchema, SchemaScript } from "../components/shared/SEOHelper";
import { BookwhenEmbed } from "../components/shared/BookwhenEmbed";

// Main Retreats Page Component - Hub page for all retreats
const RetreatsPage = () => {
  usePageMeta({
    title: "Retreats with Annelie Lily | Yoga, Healing & Transformation",
    description: "Yoga retreats with Annelie Lily — day, weekend, seasonal and immersive retreat journeys in the UK, Egypt and Spain.",
    canonical: "https://annelielily.com/retreats"
  });

  const serviceSchema = generateServiceSchema({
    name: "Retreats with Annelie Lily",
    description: "Yoga, healing and transformation retreats — day, weekend, seasonal and immersive 5-7 day journeys in the UK, Egypt and Spain.",
    serviceType: "Yoga Retreat",
    url: "https://annelielily.com/retreats",
    image: "https://customer-assets.emergentagent.com/job_yoga-frome/artifacts/r83k2965_image.png"
  });

  const includedItems = [
    "Yoga (Hatha, Yin, Restorative, Kundalini)",
    "Meditation and breathwork",
    "Reiki and energy healing",
    "Somatic practices and guided relaxation",
    "Sound healing or gong bath",
    "Nature walks and time outdoors",
    "Sauna, swimming or rest spaces (depending on venue)",
    "Nourishing, wholesome food (including gluten-free and plant-based options)",
    "Gentle reflection and integration",
    "Sense of community",
  ];

  const retreatExperiences = [
    {
      icon: "🌿",
      title: "Day Retreats",
      location: "Frome & Devon",
      desc: "A full day to reset without travelling far. Step out of your routine and into a space of calm, with yoga, healing, sauna (depending on the venue) and nourishing food. Perfect if you need a pause, or if you are new to retreats and would like to experience a day before committing to a full weekend."
    },
    {
      icon: "🌙",
      title: "Weekend Retreats",
      location: "UK",
      desc: "2–3 night retreats in Devon or Somerset. These retreats allow you to fully switch off, drop deeper into your body, and experience meaningful rest and transformation over a few days."
    },
    {
      icon: "☀️",
      title: "Seasonal Retreats",
      location: "Devon & beyond (Weekend and Day Retreats)",
      desc: "Working with the energy of the seasons. From Spring Equinox to Summer Solstice and into Autumn, these retreats honour natural cycles — supporting you to align with your own rhythm, energy and intentions."
    },
    {
      icon: "✨",
      title: "Retreat Journeys",
      location: "including Egypt and Spain",
      desc: "5–7 day immersive retreats. These are deeper, more transformational experiences held in energetically powerful places, combining spiritual exploration, healing practices and personal transformation."
    },
  ];

  const testimonials = [
    {
      quote: "Annelie's yin yoga is really rewarding and enjoyable. I always leave her classes feeling relaxed and completely de-stressed, both physically and mentally. Her women's circles are warm, interesting and fun, and I've made so many meaningful friendships through them. I've also attended several retreats, which have given me a real sense of belonging and a chance to step away and reset. I even joined the Spain retreat, which for me was life-changing. There is always such a beautiful sense of connection — we laugh, explore, and share experiences together. I'm so grateful for everything I've experienced and the people I've met along the way.",
    },
    {
      quote: "A truly wonderful and life-changing experience. I wasn't into yoga before the retreat, but I hoped to take something from it that I resonated with — which I did, and so much more. I enjoyed every single session over the weekend. Annelie is a wonderful teacher, and her knowledge is fascinating. She supports everyone, whatever their level or experience. Her voice is incredibly soothing and calming, and she creates a space where everyone feels comfortable. 11/10 for the whole experience.",
    },
  ];

  return (
    <div className="min-h-screen bg-cream">
      <SchemaScript schema={serviceSchema} />
      <Navigation />
      <PageBreadcrumb currentPage="Retreats" currentSlug="retreats" />

      {/* Hero Section */}
      <section className="pt-8 pb-16 px-6 md:px-12 bg-muted" data-testid="retreats-hero">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-xs uppercase tracking-[0.2em] text-gold font-semibold mb-3 block">
              Yoga • Healing • Transformation
            </span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-light text-neutral-800 mb-6">
              Retreats with Annelie Lily
            </h1>
            <p className="text-neutral-700 max-w-2xl mx-auto leading-relaxed mb-8">
              Step away from the noise of everyday life and return to yourself. My yoga retreats
              are more than just a break away — they are an invitation to slow down, soften, and
              reconnect with your body. A space to rebalance your energy, release tension, and
              deeply relax in a supportive and nurturing environment.
            </p>

            <div className="flex flex-wrap justify-center gap-4 mb-8">
              <span className="flex items-center gap-2 bg-white/70 px-4 py-2 rounded-full text-sm text-neutral-700">
                <Users className="w-4 h-4 text-gold" />
                Small groups
              </span>
              <span className="flex items-center gap-2 bg-white/70 px-4 py-2 rounded-full text-sm text-neutral-700">
                <MapPin className="w-4 h-4 text-gold" />
                UK • Egypt • Spain
              </span>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link
                to="/contact"
                className="btn-primary inline-flex items-center gap-2"
                data-testid="retreats-hero-book-btn"
              >
                Book Now <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/contact"
                className="btn-secondary"
                data-testid="retreats-hero-enquire-btn"
              >
                Register Interest
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Hero retreat group photo */}
      <section className="px-6 md:px-12 bg-cream pt-4 pb-8" data-testid="retreats-hero-photo">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="relative"
          >
            <div className="aspect-[16/9] md:aspect-[21/9] rounded-2xl overflow-hidden shadow-xl border border-gold/10">
              <img
                src="https://customer-assets.emergentagent.com/job_8928e5c6-c128-403f-9029-6531d7ff9ebf/artifacts/n2y8ah76_IMG_4824.jpg"
                alt="Joyful retreat group of around 16 participants smiling and waving in the warm loft room with fairy lights and wooden beams during an Annelie Lily retreat"
                className="w-full h-full object-cover"
                width={1600}
                height={900}
                loading="eager"
                decoding="async"
              />
            </div>
            <div className="hidden md:block absolute -top-3 -left-3 w-6 h-6 bg-gold/30 rounded-full" />
            <div className="hidden md:block absolute -bottom-4 -right-4 w-10 h-10 bg-gold/20 rounded-full" />
          </motion.div>
        </div>
      </section>

      {/* Upcoming Retreats - Bookwhen (top placement) */}
      <BookwhenEmbed
        slug="annelielily99"
        title="Upcoming Retreats"
        subtitle="See upcoming retreats and reserve your place below."
        bgClass="bg-cream"
        testId="retreats-bookwhen"
      />

      {/* Intro Continued */}
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="prose-content space-y-4 text-neutral-700 text-center"
          >
            <p>
              Held in beautiful natural settings, each retreat is carefully held to support you
              in letting go of what no longer feels aligned, and gently opening into transformation.
            </p>
            <p>
              Whether you join for a day, a weekend, or a longer journey, you will be guided
              through practices that support your whole being — body, mind and spirit.
            </p>
          </motion.div>
        </div>
      </section>

      {/* What You Can Expect */}
      <section className="py-16 px-6 md:px-12 bg-muted" data-testid="retreats-what-to-expect">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-4">
              What You Can Expect on My Retreats
            </h2>
            <p className="text-neutral-600 max-w-xl mx-auto">
              Each retreat is unique, but may include:
            </p>
          </motion.div>

          <motion.ul
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="grid md:grid-cols-2 gap-x-8 gap-y-3 max-w-3xl mx-auto"
          >
            {includedItems.map((item) => (
              <li key={item} className="flex items-start gap-3 text-neutral-700">
                <span className="w-1.5 h-1.5 bg-gold rounded-full mt-2 flex-shrink-0" />
                <span>{item}</span>
              </li>
            ))}
          </motion.ul>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center text-neutral-700 mt-10 italic max-w-2xl mx-auto"
          >
            There is always space to simply rest, be still, and take time for yourself.
          </motion.p>
        </div>
      </section>

      {/* Why Come on Retreat with Me */}
      <section className="py-16 px-6 md:px-12 bg-cream" data-testid="retreats-why-section">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-6 text-center">
              Why Come on Retreat with Me?
            </h2>
            <div className="prose-content space-y-4 text-neutral-700">
              <p>
                I have been running retreats for over 7 years and teaching yoga for over 14 years.
                Over 80% of retreat guests rebook and some attend 2–3 retreats a year.
              </p>
              <p>
                While yoga is a pathway into the body, the deeper work happens beneath the
                surface — within your energy, your emotions, and your nervous system. Time spent
                on retreat allows you to go beyond the surface, creating space to release, reset
                and heal on a deeper level.
              </p>
              <p>
                I weave together yoga, breathwork, somatic practices, energy healing and sound
                to create spaces where real transformation can take place. Each retreat is held
                with presence, care and intuition. I tune into the energy of the group, allowing
                the experience to unfold in a way that supports what is truly needed.
              </p>
              <p className="text-lg font-medium text-neutral-800">
                These are not one-size-fits-all retreats. My retreats have been carefully developed
                and refined over the years to offer a deeply nourishing and transformative experience.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Retreat Experiences */}
      <section className="py-16 px-6 md:px-12 bg-muted" data-testid="retreat-experiences">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-4">
              Retreat Experiences
            </h2>
            <p className="text-neutral-600 max-w-xl mx-auto">
              From local day retreats to immersive international journeys
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {retreatExperiences.map((retreat, index) => (
              <motion.div
                key={retreat.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white/80 p-7 rounded-2xl border border-gold/10 hover:border-gold/30 transition-all"
                data-testid={`retreat-experience-${index}`}
              >
                <div className="text-3xl mb-3" aria-hidden="true">{retreat.icon}</div>
                <h3 className="font-heading text-xl text-neutral-800 mb-1">{retreat.title}</h3>
                <p className="text-gold text-sm font-medium mb-3">{retreat.location}</p>
                <p className="text-neutral-600 text-sm leading-relaxed">{retreat.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Who Are My Retreats For */}
      <section className="py-16 px-6 md:px-12 bg-cream" data-testid="retreats-who-for">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-6 text-center">
              Who Are My Retreats For?
            </h2>
            <div className="prose-content space-y-4 text-neutral-700">
              <p>
                My retreats are open to both men and women. Many people come on their own and
                quickly feel welcomed into a warm and supportive community. This is a space
                where you can simply be yourself — and connect with others in a relaxed and
                authentic way.
              </p>
              <p>
                <strong>No previous yoga experience is required — beginners are very welcome.</strong>
              </p>
              <p>
                Many people arrive feeling tired, overwhelmed or disconnected. Over the course of
                the retreat, something begins to shift. There is more calm. More clarity. And a
                deeper sense of connection — to yourself, to others, and to what truly matters.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Retreat Gallery */}
      <section className="py-16 px-6 md:px-12 bg-muted">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-4">
              Retreat Moments
            </h2>
            <p className="text-neutral-600 max-w-xl mx-auto">
              Glimpses from past retreats in Somerset and Spain — yoga, community, and transformation
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { src: "https://customer-assets.emergentagent.com/job_e5a39abe-98a9-41c1-8b03-8654ab8086e3/artifacts/zmyrcbvy_IMG_9012.jpeg", alt: "Joyful yoga retreat group with arms raised in The Station glass pavilion surrounded by green trees in Frome" },
              { src: "https://customer-assets.emergentagent.com/job_e5a39abe-98a9-41c1-8b03-8654ab8086e3/artifacts/lxrsltn7_IMG_2010.jpg", alt: "Bliss Junkie yoga retreat group celebrating outdoors in Spanish mountains with matching white vests" },
              { src: "https://customer-assets.emergentagent.com/job_e5a39abe-98a9-41c1-8b03-8654ab8086e3/artifacts/qiaudz2r_IMG_1999.jpg", alt: "Yoga retreat group celebrating with arms raised in glass pavilion with sacred mandala altar at The Station, Frome" },
              { src: "https://customer-assets.emergentagent.com/job_e5a39abe-98a9-41c1-8b03-8654ab8086e3/artifacts/8jrk5lbf_IMG_2008%20%281%29.jpg", alt: "Yoga retreat participants practicing forward fold with bolsters in Spanish studio" },
              { src: "https://customer-assets.emergentagent.com/job_e5a39abe-98a9-41c1-8b03-8654ab8086e3/artifacts/kbpbkage_IMG_2007.jpg", alt: "Joyful yoga retreat group with arms raised on rooftop terrace in Spain with mountain views" },
              { src: "https://customer-assets.emergentagent.com/job_e5a39abe-98a9-41c1-8b03-8654ab8086e3/artifacts/w3cz59xk_IMG_2006.jpg", alt: "Golden hour yoga session with retreat group practicing arm stretches in Spanish studio" },
              { src: "https://customer-assets.emergentagent.com/job_e5a39abe-98a9-41c1-8b03-8654ab8086e3/artifacts/tya7s8ug_IMG_2001.jpg", alt: "Small retreat group celebrating with arms raised in sunlit glass studio at The Station, Frome" },
              { src: "https://customer-assets.emergentagent.com/job_e5a39abe-98a9-41c1-8b03-8654ab8086e3/artifacts/3jb394in_IMG_2005.jpg", alt: "Yoga retreat group practicing seated arm stretches at golden hour with Annelie Lily teaching" },
            ].map((img, idx) => (
              <motion.div
                key={img.src}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.05 }}
                className="aspect-square rounded-xl overflow-hidden bg-muted"
              >
                <ClickableImage
                  src={img.src}
                  alt={img.alt}
                  imgClassName="object-cover"
                  width={400}
                  height={400}
                />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials - What People Have Shared */}
      <section className="py-16 px-6 md:px-12 bg-cream" data-testid="retreats-testimonials">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <span className="text-xs uppercase tracking-[0.2em] text-gold font-semibold mb-3 block">
              🌿 What People Have Shared
            </span>
            <h2 className="font-heading text-2xl md:text-3xl text-neutral-800">
              Voices From Past Retreats
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {testimonials.map((t, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="bg-white/80 p-7 md:p-8 rounded-2xl border border-gold/10"
                data-testid={`retreat-testimonial-${idx}`}
              >
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 text-gold fill-gold" />
                  ))}
                </div>
                <p className="text-neutral-700 italic leading-relaxed">
                  &ldquo;{t.quote}&rdquo;
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Booking Section */}
      <section className="py-16 px-6 md:px-12 bg-muted" data-testid="retreats-booking">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-8"
          >
            <Sparkles className="w-8 h-8 text-gold mx-auto mb-4" />
            <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-4">
              Join a Retreat
            </h2>
            <p className="text-neutral-600 max-w-xl mx-auto">
              To find out about upcoming retreats and reserve your place, please get in touch.
              Payment plans are available.
            </p>
          </motion.div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-2">
            <Link to="/contact" className="btn-primary" data-testid="retreats-book-cta">
              Enquire to Book <ArrowRight className="w-4 h-4 inline ml-2" />
            </Link>
            <Link to="/contact" className="btn-secondary" data-testid="retreats-interest-cta">
              Register Interest
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default RetreatsPage;
